 import { ODataTable } from 'odata/ODataTable';
import { failString, Fetcher } from 'query/Fetch';
import Schema from 'query/Schema';
import TableColumn from 'table/TableColumn';
import { ColumnType } from 'query/ColumnType';
import { PrimaryKey } from 'table/Table';


export function fromURL(fetcher: Fetcher, url: string): Promise<Schema[]> {
    return new Promise<Schema[]>((resolve, reject) => {
        fetcher.fetchXML(metadataURL(url))
            .then(
                xml => {
                    try {
                        if (!isOData(xml)) {
                            console.error(xml);
                            reject(failString(`Service at ${metadataURL(url)} did not respond with an OData metadata document.`));
                            return;
                        }

                        let result : Schema[];
                        result = parseODataEDMDocument(url, xml);
                        resolve(result);
                    } catch (error) {
                        console.error(error);
                        reject(error);
                    }
                },
                error => {
                    reject(error);
                }
            );
    });
}

type Namespace = string | null;

export function parseODataEDMDocument(url: string, xml: Document) {
    let result: Schema[] = [];

    // There can be multiple schemas.
    let schemaXML: HTMLCollectionOf<Element> = getSchemaElements(xml);

    let entityTypes: Map<string, TableEntry>; // fully quantified name -> table columns.
    entityTypes = readEntityTypes(schemaXML);

    for (let i = 0; i < schemaXML.length; i++) {
        let each = schemaXML[i];
        let ns: string | null = each.getAttribute("Namespace") ?? "";

        let newSchema: Schema = new Schema(url, ns);
        // OData schemas don't have names! The best I can do is use it's namespace.
        // TODO: Work with the OData authors to fix this.
        newSchema.name = ns;
        result.push(newSchema);
        populateSchema(newSchema, each, entityTypes);
    }

    // I would like to keep the queries on the same server. Until that stage:
    for (let each of result) {
        each.generateStartingQueries();
    }

    return result;
}

function getSchemaElements(xml: Document) {
    return xml
        .getElementsByTagName("edmx:Edmx")[0]
        .getElementsByTagName("edmx:DataServices")[0]
        .getElementsByTagName("Schema");
}

/** Return the URL where the document metadata can be retrieved. */
export function metadataURL(baseURL: string): URL {
    return new URL(toFullURL(baseURL) + "$metadata");
}

export function tableURL(baseURL: string, tableName: string) {
    return baseURL + tableName;
}

export function toFullURL(inStr: string): string {
    let result;
    // Surely there's a better way of doing this?
    if (inStr.startsWith("http")) result = inStr;
    else result = window.location.protocol + "//" + window.location.hostname + ":" + window.location.port + inStr
    return result;
}

/* I have tried to find a DTD generator and failed.
*/
/* Read the tables from the OData XML schema and populate the schema. */
function populateSchema(
    schema: Schema,
    schemaElement: Element,
    entityTypes: Map<string, TableEntry>) {

    let tablesXML = schemaElement
        ?.getElementsByTagName("EntityContainer")[0]
        ?.getElementsByTagName("EntitySet");

    if (undefined===tablesXML) {
        // There is no EntityContainer element.
        return;
    }

    for (let i = 0; i < tablesXML.length; i++) {
        let each = tablesXML[i];

        let tableName: string | null = each.getAttribute("Name");
        if (null === tableName) {
            console.log("Table name missing from OData schema.");
            continue;
        }

        let tableType: TableEntry;
        let tableTypeFQN = each.getAttribute("EntityType");
        if (tableTypeFQN) {
            let tableTypeOrUndefined = entityTypes.get(tableTypeFQN);
            if (tableTypeOrUndefined) {
                tableType = tableTypeOrUndefined;
            } else {
                console.error(`Could not find table ${tableTypeFQN}`);
                continue;
            }
        } else {
            console.error(`EntityContainer element ${String(each)} has no EntityType.`);
            continue;
        }


        let table: ODataTable = new ODataTable(schema.url, tableName);
        table.columns = tableType.columns;
        if (null!==tableType.primaryKey) {
            table.primaryKey = tableType.primaryKey;
        }
        schema.add(table);
    }
}

interface TableEntry {
    primaryKey: PrimaryKey|null;
    columns: TableColumn;
}

function isOData(xml:Document) {
    let edmx = xml.getElementsByTagName("edmx:Edmx");
    return (edmx && edmx.length > 0)
}

function readEntityTypes(schemas: HTMLCollectionOf<Element>) {
    let result = new Map<string, TableEntry>();

    // We need to do two passes. stripNamespace(ns, 
    // The first pass makes the entities with primitive columns.
    // The second pass is for the relationships between the entities.

    // Add the primitive columns.
    for (let j = 0; j < schemas.length; j++) {
        let eachSchema = schemas[j];
        let namespace: Namespace = eachSchema.getAttribute("Namespace");
        let entities = eachSchema.getElementsByTagName("EntityType");

        for (let i = 0; i < entities.length; i++) {
            let entity = entities[i];
            let tableName = entity.getAttribute("Name");
            if (null === tableName) {
                console.warn("Skipping unnamed EntityType.");
                continue;
            }
            let columns: TableColumn = new TableColumn(tableName, ColumnType.RootColumn);

            let properties = entity.children;
            for (let i = 0; i < properties.length; i++) {
                let each = properties[i];
                switch (each.nodeName) {
                    case 'Property':
                        let newColumn = createColumn(each, result, namespace);
                        columns.children.push(newColumn);
                        break;
                    // These will be processed below...
                    case 'NavigationProperty':
                    case 'Key':
                        break;
                    default:
                        //console.log('TODO: implement node name: ' + each.nodeName);
                }
            }
            let tableEntry : TableEntry = {
                primaryKey: null, // will be replaced later.
                columns: columns
            };
            result.set(fullyQuantifiedName(namespace, tableName), tableEntry);
        }
    }

    // Add the navigation / referenced properties.
    // We couldn't do this until the entity type dictionary is populated.
    for (let j = 0; j < schemas.length; j++) {
        let eachSchema = schemas[j];
        let namespace: Namespace = eachSchema.getAttribute("Namespace");
        let entities = eachSchema.getElementsByTagName("EntityType");

        for (let i = 0; i < entities.length; i++) {
            let entity = entities[i];
            let name = entity.getAttribute("Name");
            if (null === name) {
                console.log(`Navigation column has no name.`);
                continue;
            }
            let tableEntry = result.get(fullyQuantifiedName(namespace, name));
            if (!tableEntry) {
                throw new Error("Impossible."); // It was created above.
            }
            let properties = entity.children;
            for (let i = 0; i < properties.length; i++) {
                let each = properties[i];
                switch (each.nodeName) {
                    case 'Property':
                        // Already processed above.
                        break;
                    case 'NavigationProperty':
                        let newColumn = createCompoundColumn(name, each, result, namespace);
                        if (newColumn) {
                            tableEntry.columns.children.push(newColumn);
                        }
                        break;
                    case 'Key':
                        let pk : PrimaryKey = readPrimaryKey(each, tableEntry);
                        tableEntry.primaryKey = pk;
                        break;
                    default:
                        //console.log('TODO: implement node: ' + each.nodeName);
                }

            }
        }
    }

    return result;
}

function readPrimaryKey(node: Element, tableEntry: TableEntry) : PrimaryKey {
    let pkColumns : Array<TableColumn> = [];
    let refs = node.getElementsByTagName("PropertyRef")
    for (let i=0; i<refs.length; i++) {
        let ref = refs[i];
        let pkName = ref.getAttribute("Name");
        if (!pkName) {
            console.error("Primary key has no name.");
            continue;
        }
        let pkColumn = tableEntry.columns.findColumn(pkName);
        if (!pkColumn) {
            console.error(`Could not find primary key "${pkName}"`);
            continue;
        }
        pkColumns.push(pkColumn);
    }
    let result : PrimaryKey = new PrimaryKey(pkColumns);
    return result;
}

function fullyQuantifiedName(namespace: Namespace, name: string) {
    return (!namespace) ? name : (namespace + '.' + name);
}

function createColumn(node: Element, entityTypes: Map<string, TableEntry>, ns: Namespace): TableColumn {
    let name: string;
    let typeString: string;

    // Pull out the name.
    name = node.getAttribute("Name") ?? "Unnamed Column";
    typeString = node.getAttribute("Type") ?? "Edm.String";
    let typeStringC = typeStringFromCollectionType(typeString);
    if (typeStringC !== null) {
        typeString = typeStringC;
    }

    let type = toColumnType(typeString);
    if (null === type) {
        let newColumn = new TableColumn(name, ColumnType.ParentColumn);
        let f = entityTypes.get(typeString);
        if (f) {
            newColumn.children = f.columns.children;
        } else {
            console.error(`Could not find table ${typeString}`);
        }
        return newColumn;
    } else {
        return new TableColumn(name, type);
    }
}

function createCompoundColumn(tableName: string, node: Element, entityTypes: Map<string, TableEntry>, ns: Namespace): TableColumn | null {
    let name = node.getAttribute("Name") ?? "Unnamed Column";
    let result: TableColumn;
    let isCollection: boolean = false;

    // Find the typeString.
    let typeString;
    {
        let rawTypeString = node.getAttribute("Type") ?? "Edm.String";
        let typeStringCollection = typeStringFromCollectionType(rawTypeString); // null if not "Collection(...)"
        if (null === typeStringCollection) {
            typeString = rawTypeString;
        } else {
            typeString = typeStringCollection;
            isCollection = true;
        }
    }

    let type = toColumnType(typeString);

    if (null === type) {
        // The type is one of the EntityTypes.
        result = new TableColumn(name, ColumnType.ParentColumn);
        result.collection = isCollection;
        let r = entityTypes.get(typeString);
        if (!r) {
            console.error(`Could not find referenced table ${typeString}`);
            return null;
        }
        // We use the same array. This avoids going into a recursive loop here.
        result.children = r.columns.children;
    } else {
        // The type is a primitive type.
        result = new TableColumn(name, type);
        result.collection = isCollection;
    }

    // Hide the foreign keys columns.
    let referentialConstraints = node.getElementsByTagName("ReferentialConstraint");
    if (referentialConstraints.length > 0) {
        let r = referentialConstraints[0];
        let fkFrom = r.getAttribute("Property"); // The column that is the fk in my table.
        //let fkTo = r.getAttribute("ReferencedProperty"); // The column in the other table. FQN.
        // the remote key in fkTo can be hidden too... if you want to implement that here.
        if (null !== fkFrom) {
            let myTable = entityTypes.get(fullyQuantifiedName(ns, tableName))?.columns;
            let localColumn = myTable?.findColumn(fkFrom);
            if (!localColumn) {
                console.error(`Could not find column ${fkFrom}`);
            } else {
                localColumn.hidden = true; // c is a foreign key and shouldn't be shown to the user.
            }
        }
    }

    return result;
}

function typeStringFromCollectionType(s: string) {
    const collection = "Collection(";
    if (s.startsWith(collection)) {
        let t: string = s;
        t = s.substring(collection.length, s.length);
        if (t.endsWith(")")) {
            t = t.substring(0, t.length - 1);
        }
        return t;
    } else {
        return null;
    }
}

function toColumnType(type: string): ColumnType | null {

    switch (type) {
        case 'Edm.Binary': return ColumnType.Binary
        case 'Edm.Boolean': return ColumnType.Boolean
        case 'Edm.Byte': return ColumnType.Byte
        case 'Edm.Date': return ColumnType.Date
        case 'Edm.DateTimeOffset': return ColumnType.DateTimeOffset
        case 'Edm.Decimal': return ColumnType.Decimal
        case 'Edm.Double': return ColumnType.Double
        case 'Edm.Duration': return ColumnType.Duration
        case 'Edm.Guid': return ColumnType.Guid
        case 'Edm.Int16': return ColumnType.Int16
        case 'Edm.Int32': return ColumnType.Int32
        case 'Edm.Int64': return ColumnType.Int64
        case 'Edm.SByte': return ColumnType.SByte
        case 'Edm.Single': return ColumnType.Single
        case 'Edm.Stream': return ColumnType.Stream
        case 'Edm.String': return ColumnType.String
        case 'Edm.TimeOfDay': return ColumnType.TimeOfDay
        case 'Edm.Geography': return ColumnType.Geography
        case 'Edm.GeographyPoint': return ColumnType.GeographyPoint
        case 'Edm.GeographyLineString': return ColumnType.GeographyLineString
        case 'Edm.GeographyPolygon': return ColumnType.GeographyPolygon
        case 'Edm.GeographyMultiPoint': return ColumnType.GeographyMultiPoint
        case 'Edm.GeographyMultiLineString': return ColumnType.GeographyMultiLineString
        case 'Edm.GeographyMultiPolygon': return ColumnType.GeographyMultiPolygon
        case 'Edm.GeographyCollection': return ColumnType.GeographyCollection
        case 'Edm.Geometry': return ColumnType.Geometry
        case 'Edm.GeometryPoint': return ColumnType.GeometryPoint
        case 'Edm.GeometryLineString': return ColumnType.GeometryLineString
        case 'Edm.GeometryPolygon': return ColumnType.GeometryPolygon
        case 'Edm.GeometryMultiPoint': return ColumnType.GeometryMultiPoint
        case 'Edm.GeometryMultiLineString': return ColumnType.GeometryMultiLineString
        case 'Edm.GeometryMultiPolygon': return ColumnType.GeometryMultiPolygon
        case 'Edm.GeometryCollection': return ColumnType.GeometryCollection
        default: return null;
    }
}